/**
 * REEDOS Knowledge Base — global context-sensitive help button (?).
 *
 * A floating action button anchored to the viewport (`kb-help-fab`
 * CSS class) that opens a right-side drawer with the article mapped
 * to the current frontend route. If nothing maps, the drawer degrades
 * gracefully to a search prompt.
 *
 * Placement: mount ONCE at the app root (see App.js). The button is
 * hidden on the /kb/* routes themselves (redundant) and on print
 * media (kb.css). Not tied to any specific product — works across
 * Workforce, Commercial Builder, Operational Projects, etc.
 */
import { useLocation, Link } from "react-router-dom";
import { useState } from "react";
import { HelpCircle, ExternalLink, X, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useKbContext, kbRecordView } from "@/lib/kb";
import { useAuth } from "@/contexts/AuthContext";

// Routes on which the help FAB is always suppressed.
const _SUPPRESS_ROUTES = [
    "/login",
    "/forgot-password",
    "/reset-password",
    "/accept",
    "/kb",
];

export default function HelpButton() {
    const location = useLocation();
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const currentRoute = normaliseRoute(location.pathname);
    // Only call the KB context endpoint when the drawer is open —
    // avoids sending a network request on every navigation.
    const { data, loading } = useKbContext(open ? currentRoute : null);

    // Do not render for unauthenticated visitors or on suppressed routes.
    if (!user) return null;
    if (_SUPPRESS_ROUTES.some((p) => location.pathname.startsWith(p))) {
        return null;
    }

    const primary = data?.primary;
    const related = data?.related || [];

    return (
        <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
                <Button
                    className="kb-help-fab"
                    size="icon"
                    aria-label="Open context-sensitive help"
                    data-testid="kb-help-fab"
                    onClick={() => {
                        if (!open) {
                            // Prefetch happens through useKbContext once open=true.
                        }
                    }}
                >
                    <HelpCircle className="h-5 w-5" />
                </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
                <SheetHeader>
                    <SheetTitle className="flex items-center gap-2">
                        <HelpCircle className="h-5 w-5 text-primary" />
                        Help for this page
                    </SheetTitle>
                </SheetHeader>
                <div className="mt-4">
                    {loading && (
                        <div className="text-sm text-muted-foreground" data-testid="kb-help-loading">
                            Looking up help…
                        </div>
                    )}
                    {!loading && !primary && (
                        <div
                            className="rounded-md border border-dashed border-border p-4 bg-muted/40"
                            data-testid="kb-help-no-mapping"
                        >
                            <p className="text-sm text-muted-foreground">
                                No documentation is directly linked to
                                <code className="mx-1 px-1 py-0.5 bg-background rounded text-xs">
                                    {currentRoute}
                                </code>
                                yet.
                            </p>
                            <Link
                                to="/kb"
                                onClick={() => setOpen(false)}
                                className="text-primary text-sm mt-3 inline-flex items-center gap-1 hover:underline"
                                data-testid="kb-help-browse-link"
                            >
                                <Search className="h-4 w-4" />
                                Browse the Knowledge Base
                            </Link>
                        </div>
                    )}
                    {!loading && primary && (
                        <div data-testid="kb-help-primary">
                            <Link
                                to={`/kb/article/${primary.slug}`}
                                onClick={() => {
                                    kbRecordView({
                                        article_slug: primary.slug,
                                        source: "context_help",
                                    });
                                    setOpen(false);
                                }}
                                className="block rounded-md border border-primary/40 bg-primary/5 p-4 hover:bg-primary/10"
                                data-testid={`kb-help-primary-${primary.slug}`}
                            >
                                <div className="font-medium text-primary flex items-center gap-1">
                                    <ExternalLink className="h-4 w-4" />
                                    {primary.title}
                                </div>
                                <div className="text-xs text-muted-foreground mt-1">
                                    {primary.product} · {primary.module || "—"}
                                </div>
                            </Link>
                            {related.length > 0 && (
                                <div className="mt-4">
                                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                                        Related
                                    </div>
                                    <ul className="space-y-1">
                                        {related.map((r) => (
                                            <li key={r.slug}>
                                                <Link
                                                    to={`/kb/article/${r.slug}`}
                                                    onClick={() => setOpen(false)}
                                                    className="block text-sm text-primary hover:underline"
                                                    data-testid={`kb-help-related-${r.slug}`}
                                                >
                                                    {r.title}
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </SheetContent>
        </Sheet>
    );
}

/**
 * Normalise a location.pathname so it matches how article authors
 * write context_routes[]. Rules:
 *   · trim trailing slash
 *   · replace concrete ID segments with `:id` when they look like a
 *     UUID or a REEDOS-style prefixed code (`EMP-…`, `USR-…`).
 * The heuristic favours false negatives (leave path alone) over
 * false positives — we'd rather show a "no mapping" state than a
 * mis-directed article.
 */
function normaliseRoute(pathname) {
    if (!pathname || pathname === "/") return "/";
    let p = pathname.replace(/\/+$/, "");
    // Replace UUID-like segments
    p = p.replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, "/:id");
    // Replace REEDOS coded ids: PRW-2026-000001, EMP-123, USR-1234ABCD, etc.
    p = p.replace(/\/[A-Z]{2,5}-[A-Za-z0-9-]+/g, "/:id");
    // Replace pure-numeric segments
    p = p.replace(/\/\d+\b/g, "/:id");
    return p;
}
